import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  BuildingAPI,
  FloorAPI,
  NavigationAPI,
  ShapeAPI,
  PathClosureAPI,
  resolveAsset,
} from "../services/api";
import LeafletFloorMap from "./LeafletFloorMap";

/**
 * Modal ให้ user เลือก "ตำแหน่งทางปิด" บนแผนที่
 *
 * Props:
 *   defaultBuildingId, defaultFloorId  — preset อาคาร/ชั้น (จาก URL ของหน้า map)
 *   onCancel()
 *   onConfirm({ floorId, x, y, floorName, buildingName })
 */
export default function ClosurePickerMap({
  defaultBuildingId,
  defaultFloorId,
  onCancel,
  onConfirm,
}) {
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [buildingId, setBuildingId] = useState(defaultBuildingId || "");
  const [floorId, setFloorId] = useState(defaultFloorId || "");
  const [floorData, setFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [existingClosures, setExistingClosures] = useState([]);
  const [picked, setPicked] = useState(null); // {x,y}
  const [loading, setLoading] = useState(false);

  // โหลดอาคาร
  useEffect(() => {
    BuildingAPI.list({ pageSize: 50 })
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.items || [];
        setBuildings(list);
        if (!buildingId && list.length > 0) setBuildingId(String(list[0].id));
      })
      .catch(() => setBuildings([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // โหลดชั้น
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloors(list);
        const exists = list.some((f) => String(f.id) === String(floorId));
        if (!exists && list.length > 0) setFloorId(String(list[0].id));
      })
      .catch(() => setFloors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // โหลด floor data + shapes + existing closures
  useEffect(() => {
    if (!floorId) {
      setFloorData(null);
      setShapes([]);
      setExistingClosures([]);
      setPicked(null);
      return;
    }
    setLoading(true);
    setPicked(null);
    Promise.all([
      NavigationAPI.getFloor(floorId).catch(() => null),
      ShapeAPI.listByFloor(floorId).catch(() => []),
      PathClosureAPI.listByFloor(floorId, "active").catch(() => []),
    ])
      .then(([fd, sh, cl]) => {
        setFloorData(fd);
        setShapes(Array.isArray(sh) ? sh : []);
        setExistingClosures(Array.isArray(cl) ? cl : cl?.items || []);
      })
      .finally(() => setLoading(false));
  }, [floorId]);

  const currentBuilding = buildings.find(
    (b) => String(b.id) === String(buildingId)
  );
  const currentFloor = floors.find((f) => String(f.id) === String(floorId));
  const imageUrl = resolveAsset(floorData?.imageUrl) || null;

  // markers: existing closures (สีจาง) + picked point (ชัด)
  const markers = useMemo(() => {
    const out = existingClosures.map((c, i) => ({
      id: `existing-closure-${c.id || i}`,
      type: "closure",
      x: c.x,
      y: c.y,
      label: "ทางปิด (มีอยู่แล้ว)",
    }));
    if (picked) {
      out.push({
        id: "picked",
        type: "closure",
        x: picked.x,
        y: picked.y,
        label: "ตำแหน่งใหม่",
        permanent: true,
      });
    }
    return out;
  }, [existingClosures, picked]);

  const onMapClick = (pxCoord) => {
    setPicked(pxCoord);
  };

  const onConfirmClick = () => {
    if (!picked || !floorId) return;
    onConfirm?.({
      floorId,
      x: picked.x,
      y: picked.y,
      floorName: currentFloor?.name || `ชั้น ${currentFloor?.level || ""}`,
      buildingName: currentBuilding?.name || "",
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[5000] bg-black/70 flex flex-col">
      {/* header */}
      <div className="bg-white px-4 py-3 flex items-center gap-3 shadow">
        <button
          onClick={onCancel}
          className="text-slate-500 text-xl px-2"
          aria-label="ยกเลิก"
        >
          ✕
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800 text-sm">
            แตะตำแหน่งทางปิดบนแผนที่
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            แตะอีกครั้งเพื่อย้ายตำแหน่ง
          </div>
        </div>
      </div>

      {/* selectors */}
      <div className="bg-white px-4 pb-3 flex gap-2">
        <select
          value={buildingId}
          onChange={(e) => setBuildingId(e.target.value)}
          className="flex-1 h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white"
        >
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select
          value={floorId}
          onChange={(e) => setFloorId(e.target.value)}
          className="w-32 h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white"
        >
          {floors.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name || `ชั้น ${f.level}`}
            </option>
          ))}
        </select>
      </div>

      {/* map */}
      <div className="flex-1 relative bg-slate-100">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center text-slate-500 text-sm bg-white/60">
            กำลังโหลดแผนที่...
          </div>
        )}
        <LeafletFloorMap
          imageUrl={imageUrl}
          imageWidth={floorData?.imageWidth}
          imageHeight={floorData?.imageHeight}
          shapes={shapes}
          markers={markers}
          onMapClick={onMapClick}
          className="w-full h-full"
        />
      </div>

      {/* footer */}
      <div className="bg-white p-3 flex gap-2 border-t border-slate-200">
        <button
          onClick={onCancel}
          className="flex-1 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold"
        >
          ยกเลิก
        </button>
        <button
          onClick={onConfirmClick}
          disabled={!picked}
          className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50"
        >
          {picked
            ? `ยืนยันตำแหน่ง (${Math.round(picked.x)}, ${Math.round(picked.y)})`
            : "แตะบนแผนที่ก่อน"}
        </button>
      </div>
    </div>,
    document.body
  );
}
